import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  authRequest,
  authGoogleRequest,
  forgotRequest,
  passwordRequest,
  validationRequest,
  systemSigninRequest,
  signUpRequest,
  emailValidateRquest,
  domainRequest,
  newSigninRequest,
} from 'core/services/authentication';

import { IGroupPermissionResponse, IPlayerResponse, ITokenAction } from 'types/action/player';
import { LoadingState, SavingState } from 'types/domain/base';
import { Role, IRoleConfig } from 'types/domain/role';
import { IOrgUserVariable } from 'types/domain/user';
import { IKeyArray } from 'types/IKeyArray';
import { AuthState, IAuthResponse, IName, IResetAction, IValidateResponse, ValidateState } from 'types/session/auth';
import { PersistedState } from 'redux-persist';
import { ISession, ISessionAttemptDAO } from 'types/domain/session';
import { IOrganisationConfigDAO, OrganisationStatus } from 'types/domain/organisation';
import { hydrate } from 'core/actions/product/state';
import { IError } from 'types/app/error';
import { PasswordType } from 'app/components/Password';
import { IGroup } from 'types/domain/group';
import { preview } from './register';
import {
  actionRequest,
  configRequest,
  dialogueRequest,
  groupPermissionRequest,
  searchRequest,
  tokenRequest,
} from 'core/services/product';

export interface ISessionReducer extends PersistedState {
  name: IName;
  email: string;
  avatar: string;
  auth: Auth;
  validate: Validate;
  signup: ISignUp;
  role: Role;
  courseId: number;
  roleConfig: IRoleConfig;
  jwt: string;
  refresh: string;
  expires: string;
  tokens: IKeyArray<IToken>;
  inviteTokens: IKeyArray<string>;
  variables: IKeyArray<IOrgUserVariable>;
  attempts: IKeyArray<ISessionAttemptDAO>;
  groups: IKeyArray<IGroup>;
  mode: Mode;
  session: ISession;
  config: Partial<IOrganisationConfigDAO>;
  homeNodeId: number;
  passwordTokens: IKeyArray<IPasswordToken>;
  domainId: number;
  error: IError;
  organisationUserId: number;
  organisationStatus: OrganisationStatus;
}

export interface ISignUp {
  state: SavingState;
  error: string;
  stage: SignUpStage;
  token: string;
}

export enum SignUpStage {
  ACCOUNT = 0,
  CONFIRM = 1,
  PASSWORD = 2,
  ORGANISATION = 3,
  COMPLETE = 4,
}

export interface Auth {
  state: AuthState;
  error: string;
}
export interface Validate {
  state: ValidateState;
  error: string;
}

export enum Mode {
  LEARNING = 0,
  PREVIEW = 1,
  ADMIN = 2,
}

export enum TokenErrorStatus {
  PRIVATE = 0,
  SCORM = 1,
  DRAFT = 2,
}

export interface IToken {
  token: string;
  entityToken?: string;
  inviteToken?: string;
  courseProgressId?: number;
  moduleId?: number;
  sessionAttemptId?: number;
  actionId?: number;
  loading?: LoadingState;
  saving?: SavingState;
  created?: number;
  index?: number;
  state?: TokenState;
  message?: string;
  scorm?: boolean;
  static?: boolean;
  renew?: boolean;
  status?: TokenErrorStatus;
}

export enum TokenState {
  NEW = 0,
  PROGRESS = 1,
  CAN_RESTART = 2,
  SHOW_RESTART = 3,
}

export interface IPasswordToken {
  token: string;
  email?: string;
  response?: string;
  loading?: LoadingState;
  saving?: SavingState;
}

const initialState: ISessionReducer = {
  name: null,
  auth: {
    state: AuthState.NOT_LOGGED_IN,
    error: null,
  },
  validate: {
    state: ValidateState.NOT_VALIDATED,
    error: null,
  },
  signup: {
    stage: SignUpStage.ACCOUNT,
    state: null,
    error: null,
    token: null,
  },
  jwt: null,
  expires: null,
  refresh: null,
  role: null,
  roleConfig: null,
  courseId: null,
  tokens: {},
  inviteTokens: {},
  variables: {},
  attempts: {},
  groups: {},
  mode: Mode.LEARNING,
  _persist: null,
  session: null,
  config: null,
  homeNodeId: null,
  passwordTokens: {},
  domainId: null,
  error: null,
  email: null,
  avatar: null,
  organisationUserId: null,
  organisationStatus: null,
};

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setVariable(state, action: PayloadAction<IOrgUserVariable>) {
      state.variables[action.payload.id] = action.payload;
    },
    setGroup(state, action: PayloadAction<IGroup>) {
      state.groups[action.payload.id] = action.payload;
    },
    setAttempt(state, action: PayloadAction<ISessionAttemptDAO>) {
      state.attempts[action.payload.id] = action.payload;
    },
    removeGroup(state, action: PayloadAction<number>) {
      delete state.groups[action.payload];
    },
    updateMode(state, action: PayloadAction<Mode>) {
      state.mode = action.payload;
    },

    logout(state) {
      state.jwt = null;
      state.refresh = null;
    },
    restartToken(state, action: PayloadAction<IToken>) {
      state.tokens[action.payload.token].state = TokenState.NEW;
      state.tokens[action.payload.token].loading = LoadingState.LOADING;
    },
    updateToken(state, action: PayloadAction<IToken>) {
      const { token } = action.payload as IToken;

      state.tokens[token] = state.tokens[token]
        ? {
            ...state.tokens[token],
            ...action.payload,
          }
        : {
            loading: LoadingState.DEFAULT,
            ...action.payload,
            token,
            created: new Date().getTime(),
          };
    },
    updateTokenState(state, action: PayloadAction<IToken>) {
      const { token } = action.payload as IToken;
      state.tokens[token] = {
        ...state.tokens[token],
        ...action.payload,
      };
    },
    setError(state, action: PayloadAction<IError>) {
      state.error = action.payload;
    },
    clearError(state, action: PayloadAction<IError>) {
      state.error = null;
    },
    clearCache() {
      return initialState;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(hydrate, (state, action) => {
        if (action.payload.session) {
          Object.keys(action.payload.session)
            .filter(type => !['search', 'config', 'passwordToken'].includes(type))
            .forEach(
              type =>
                (state[type] = action.payload.session[type]
                  ? typeof action.payload.session[type] === 'object'
                    ? { ...state[type], ...action.payload.session[type] }
                    : action.payload.session[type]
                  : state[type]),
            );
          state.validate.state = ValidateState.NOT_VALIDATED;
        }
      })
      .addCase(validationRequest.pending, state => {
        state.validate.state = ValidateState.VALIDATING;
        state.validate.error = null;
      })
      .addCase(validationRequest.rejected, (state, action) => {
        state.validate.state = ValidateState.ERROR;
        state.validate.error = action.error.message;
        if (process.env.NODE_ENV !== 'development') state.jwt = null;
      })
      .addCase(validationRequest.fulfilled, (state, action) => {
        const { token, refresh, role, expires, organisationStatus, organisationUserId, courseId } =
          action.payload as IValidateResponse;
        state.validate.state = ValidateState.VALIDATED;
        state.validate.error = null;
        state.jwt = token;
        state.refresh = refresh;
        state.role = role;
        state.expires = expires;
        state.courseId = courseId;
        state.organisationStatus = organisationStatus;
        state.organisationUserId = organisationUserId;
      })

      .addCase(authRequest.pending, state => {
        state.auth.state = AuthState.LOGGING_IN;
        state.auth.error = null;
      })
      .addCase(authRequest.rejected, (state, action) => {
        state.auth.state = AuthState.ERROR;
        state.auth.error = action.error.message;
      })
      .addCase(authRequest.fulfilled, (state, action) => {
        const {
          role,
          roleConfig,
          name,
          email,
          avatar,
          token,
          refresh,
          domainId,
          expires,
          organisationUserId,
          organisationStatus,
          courseId,
        } = action.payload as IAuthResponse;

        state.auth.state = AuthState.LOGGED_IN;
        state.auth.error = null;
        state.validate.state = ValidateState.VALIDATED;
        state.validate.error = null;
        state.role = role;
        state.roleConfig = roleConfig;
        state.name = name;
        state.jwt = token;
        state.courseId = courseId;
        state.refresh = refresh;
        state.domainId = domainId;
        state.expires = expires;
        state.email = email;
        state.avatar = avatar;
        state.organisationUserId = organisationUserId;
        state.organisationStatus = organisationStatus;
      })
      .addCase(authGoogleRequest.pending, state => {
        state.auth.state = AuthState.LOGGING_IN;
        state.auth.error = null;
      })
      .addCase(authGoogleRequest.rejected, (state, action) => {
        state.auth.state = AuthState.ERROR;
        state.auth.error = action.error.message;
      })
      .addCase(authGoogleRequest.fulfilled, (state, action) => {
        const {
          role,
          roleConfig,
          courseId,
          name,
          email,
          avatar,
          token,
          refresh,
          domainId,
          organisationUserId,
          organisationStatus,
        } = action.payload as IAuthResponse;

        state.auth.state = AuthState.LOGGED_IN;
        state.auth.error = null;
        state.validate.state = ValidateState.VALIDATED;
        state.validate.error = null;
        state.role = role;
        state.roleConfig = roleConfig;
        state.name = name;
        state.jwt = token;
        state.courseId = courseId;
        state.refresh = refresh;
        state.domainId = domainId;
        state.email = email;
        state.avatar = avatar;
        state.organisationUserId = organisationUserId;
        state.organisationStatus = organisationStatus;
      })
      .addCase(restartToken, (state, action) => {
        const { token } = action.payload;
        state.tokens[token] = {};
      })
      .addCase(tokenRequest.pending, (state, action) => {
        const { token, restart } = action.meta.arg as ITokenAction;
        state.tokens[token] = {
          ...state.tokens[token],
          loading: LoadingState.LOADING,
        };
      })
      .addCase(tokenRequest.rejected, (state, action) => {
        const { token } = action.meta.arg as ITokenAction;
        state.tokens[token] = { loading: LoadingState.ERROR, error: action.error };
      })
      .addCase(tokenRequest.fulfilled, (state, action) => {
        const { token, inviteToken } = action.meta.arg as ITokenAction;
        const { session, playerResponse, message, status } = action.payload;
        const activeToken: string = token || playerResponse?.token;

        if (inviteToken) state.inviteTokens[inviteToken] = activeToken;

        state.tokens[activeToken] = {
          ...(state.tokens[activeToken] ? { ...state.tokens[activeToken] } : {}),
          courseProgressId: playerResponse?.courseProgressId,
          loading: playerResponse ? LoadingState.LOADED : LoadingState.ERROR,
          static: playerResponse?.static,
          status,
          message,
        };

        if (session && (!state.session || state.session.id !== session.id)) state.session = session;

        mergeRegister(state, playerResponse);
      })
      .addCase(systemSigninRequest.fulfilled, (state, action) => {
        const { token } = action.meta.arg as ITokenAction;
        //@ts-ignore
        const { session, playerResponse, message } = action.payload;

        state.tokens[token] = {
          ...(state.tokens[token] ? { ...state.tokens[token] } : {}),
          courseProgressId: playerResponse?.courseProgressId,
          loading: playerResponse ? LoadingState.LOADED : LoadingState.ERROR,
          message,
        };

        if (session && (!state.session || state.session.id !== session.id)) state.session = session;
      })
      .addCase(actionRequest.fulfilled, (state, action) => {
        const playerResponse = action.payload as IPlayerResponse;

        mergeRegister(state, playerResponse);
      })
      // .addCase(homeRequest.fulfilled, (state, action) => {
      //   //const playerResponse = action.payload as IPlayerResponse;

      //   mergeRegister(state, action.payload);
      // })
      .addCase(dialogueRequest.fulfilled, (state, action) => {
        const playerResponse = action.payload as IPlayerResponse;
        if (playerResponse.auth) {
          state.jwt = playerResponse.auth.token;
          state.refresh = playerResponse.auth.refresh;
        }
        mergeRegister(state, playerResponse);
      })
      .addCase(groupPermissionRequest.fulfilled, (state, action) => {
        const { permission } = action.payload as IGroupPermissionResponse;
        state.tokens[permission.token] = { id: permission.id };
      })
      .addCase(configRequest.pending, (state, action) => {
        state.config = { loading: LoadingState.LOADING };
      })
      .addCase(configRequest.fulfilled, (state, action) => {
        state.config = { ...action.payload, loading: LoadingState.LOADED };
      })
      .addCase(configRequest.rejected, (state, action) => {
        state.config = { loading: LoadingState.ERROR };
      })

      .addCase(forgotRequest.pending, (state, action) => {
        const { token } = action.meta.arg as IResetAction;
        state.passwordTokens[token] = { ...state.passwordTokens[token], saving: SavingState.SAVING };
      })
      .addCase(forgotRequest.rejected, (state, action) => {
        const { token } = action.meta.arg as IResetAction;
        state.passwordTokens[token] = {
          ...state.passwordTokens[token],
          saving: SavingState.ERROR,
          response: action.error.message,
        };
      })
      .addCase(forgotRequest.fulfilled, (state, action) => {
        const { token } = action.meta.arg as IResetAction;
        state.passwordTokens[token] = {
          ...state.passwordTokens[token],
          saving: SavingState.SAVED,
          response: action.payload,
        };
      })
      .addCase(passwordRequest.pending, (state, action) => {
        const { token } = action.meta.arg as IResetAction;
        state.passwordTokens[token] = { ...state.passwordTokens[token], saving: SavingState.SAVING };
      })
      .addCase(passwordRequest.rejected, (state, action) => {
        const { token } = action.meta.arg as IResetAction;
        state.passwordTokens[token] = {
          ...state.passwordTokens[token],
          saving: SavingState.ERROR,
          response: action.error.message,
        };
      })
      .addCase(passwordRequest.fulfilled, (state, action) => {
        const {
          role,
          roleConfig,
          name,
          token,
          courseId,
          refresh,
          email,
          avatar,
          organisationUserId,
          organisationStatus,
        } = action.payload as IAuthResponse;
        state.passwordTokens[action.meta.arg.token] = {
          ...state.passwordTokens[action.meta.arg.token],
          saving: SavingState.SAVED,
          response: action.payload,
        };
        state.auth.state = AuthState.LOGGED_IN;
        state.auth.error = null;
        state.validate.state = ValidateState.VALIDATED;
        state.validate.error = null;
        state.role = role;
        state.roleConfig = roleConfig;
        state.name = name;
        state.jwt = token;
        state.courseId = courseId;
        state.refresh = refresh;
        state.email = email;
        state.avatar = avatar;
        state.organisationUserId = organisationUserId;
        state.organisationStatus = organisationStatus;

        if (action.meta.arg.type === PasswordType.SETUP) {
          state.signup.token = null;
          state.signup.state = null;
          state.signup.stage = SignUpStage.ORGANISATION;
        }
      })
      .addCase(searchRequest.fulfilled, (state, action) => {
        if (action.payload.session && !state.session) state.session = action.payload.session;

        if (state.auth.state != AuthState.LOGGED_IN) {
          const {
            role,
            roleConfig,
            name,
            token,
            courseId,
            refresh,
            email,
            avatar,
            organisationUserId,
            organisationStatus,
          } = action.payload as IAuthResponse;
          state.auth.state = AuthState.LOGGED_IN;
          state.auth.error = null;
          state.validate.state = ValidateState.VALIDATED;
          state.validate.error = null;
          state.role = role;
          state.roleConfig = roleConfig;
          state.name = name;
          state.jwt = token;
          state.refresh = refresh;
          state.email = email;
          state.avatar = avatar;
          state.courseId = courseId;
          state.organisationUserId = organisationUserId;
          state.organisationStatus = organisationStatus;
        }
      })
      .addCase(signUpRequest.pending, state => {
        state.signup.state = SavingState.SAVING;
      })
      .addCase(signUpRequest.rejected, state => {
        state.signup.state = SavingState.ERROR;
        state.signup.error = 'Email is invalid';
      })
      .addCase(signUpRequest.fulfilled, (state, action) => {
        state.signup.state = null;
        state.signup.stage = action.meta.arg.credential ? SignUpStage.ORGANISATION : SignUpStage.CONFIRM;
      })
      .addCase(emailValidateRquest.pending, state => {
        state.signup.state = SavingState.SAVING;
      })
      .addCase(emailValidateRquest.rejected, state => {
        state.signup.state = SavingState.ERROR;
        state.signup.error = 'Code is invalid';
      })
      .addCase(emailValidateRquest.fulfilled, (state, action) => {
        state.signup.state = null;
        state.signup.stage = SignUpStage.PASSWORD;
        state.signup.token = action.payload;
      })
      .addCase(domainRequest.pending, state => {
        state.signup.state = SavingState.SAVING;
      })
      .addCase(domainRequest.rejected, state => {
        state.signup.state = SavingState.ERROR;
        state.signup.error = 'Domain name is invalid';
      })
      .addCase(domainRequest.fulfilled, (state, action) => {
        state.signup.state = null;
        state.signup.stage = SignUpStage.COMPLETE;
      })
      .addCase(newSigninRequest.fulfilled, (state, action) => {
        const {
          role,
          roleConfig,
          name,
          courseId,
          token,
          refresh,
          email,
          avatar,
          organisationStatus,
          organisationUserId,
        } = action.payload as IAuthResponse;
        state.auth.state = AuthState.LOGGED_IN;
        state.auth.error = null;
        state.validate.state = ValidateState.VALIDATED;
        state.validate.error = null;
        state.role = role;
        state.roleConfig = roleConfig;
        state.name = name;
        state.jwt = token;
        state.courseId = courseId;
        state.refresh = refresh;
        state.email = email;
        state.avatar = avatar;
        state.organisationUserId = organisationUserId;
        state.organisationStatus = organisationStatus;
      })
      .addCase(preview, (state, action) => {
        state.mode = action.payload.mode ?? Mode.PREVIEW;
      });
  },
});

function mergeRegister(state: ISessionReducer, playerResponse: IPlayerResponse) {
  if (!playerResponse) return;

  const { attemptRegister, variableRegister, homeCourseNodeId } = playerResponse;

  attemptRegister.forEach(attempt => (state.attempts[attempt.moduleId] = attempt));
  variableRegister.forEach(orgUserVariable => (state.variables[orgUserVariable.variableId] = orgUserVariable));
  state.homeNodeId = homeCourseNodeId;
}

export const {
  setVariable,
  setGroup,
  setAttempt,
  removeGroup,
  updateMode,
  logout,
  updateToken,
  updateTokenState,
  restartToken,
  clearCache,
  setError,
  clearError,
} = sessionSlice.actions;
export default sessionSlice.reducer;
